/*
 * sitemapData.js
 * ---------------------------------------------------------------------------
 * Static, documentation-only data describing the Fuel Station ERP structure
 * and its core business workflows.
 *
 * This file contains NO business logic, NO API calls and NO calculations.
 * It is consumed exclusively by the Sitemap & Documentation module to render
 * a hierarchical sitemap and a set of interactive flowcharts.
 *
 * `page` values mirror the `activePage` ids understood by App.jsx -> PageContent
 * so a node can deep-link into the live module via the existing global
 * `navigate` custom event. Nodes without a `page` are purely informational.
 *
 * Display copy is stored as i18n key references (`nameKey`, `descKey`,
 * `titleKey`, `labelKey`, `detailKey`) and resolved with `t()` in Sitemap.jsx.
 * The `id` and `page` values are logical identifiers used for navigation and
 * MUST NOT be translated.
 * ---------------------------------------------------------------------------
 */

// ── System Sitemap (mirrors the real sidebar navigation hierarchy) ──────────
export const SITEMAP = [
  { id: 'dashboard', nameKey: 'sitemap.nodes.dashboard.name', icon: '⊞', page: 'Dashboard',
    descKey: 'sitemap.nodes.dashboard.desc' },

  {
    id: 'master', nameKey: 'sitemap.nodes.master.name', icon: '📋',
    descKey: 'sitemap.nodes.master.desc',
    children: [
      { id: 'product',  nameKey: 'sitemap.nodes.product.name',  page: 'Product',      descKey: 'sitemap.nodes.product.desc' },
      { id: 'tank',     nameKey: 'sitemap.nodes.tank.name',     page: 'Tank',         descKey: 'sitemap.nodes.tank.desc' },
      { id: 'pump',     nameKey: 'sitemap.nodes.pump.name',     page: 'PumpList',     descKey: 'sitemap.nodes.pump.desc' },
      { id: 'nozzle',   nameKey: 'sitemap.nodes.nozzle.name',   page: 'NozzleConfig', descKey: 'sitemap.nodes.nozzle.desc' },
      { id: 'supplier', nameKey: 'sitemap.nodes.supplier.name', page: 'Supplier',     descKey: 'sitemap.nodes.supplier.desc' },
      { id: 'customer', nameKey: 'sitemap.nodes.customer.name', page: 'Customer',     descKey: 'sitemap.nodes.customer.desc' },
      { id: 'employee', nameKey: 'sitemap.nodes.employee.name', page: 'Employees',    descKey: 'sitemap.nodes.employee.desc' },
    ]
  },

  {
    id: 'sales', nameKey: 'sitemap.nodes.sales.name', icon: '💰',
    descKey: 'sitemap.nodes.sales.desc',
    children: [
      { id: 'salelist',   nameKey: 'sitemap.nodes.salelist.name',   page: 'SaleList',        descKey: 'sitemap.nodes.salelist.desc' },
      { id: 'meter',      nameKey: 'sitemap.nodes.meter.name',      page: 'MeterReading',    descKey: 'sitemap.nodes.meter.desc' },
      { id: 'lube',       nameKey: 'sitemap.nodes.lube.name',       page: 'LubeSales',       descKey: 'sitemap.nodes.lube.desc' },
      { id: 'lubegst',    nameKey: 'sitemap.nodes.lubegst.name',    page: 'SaleBillLubeGST', descKey: 'sitemap.nodes.lubegst.desc' },
      { id: 'credit',     nameKey: 'sitemap.nodes.credit.name',     page: 'CreditSale',      descKey: 'sitemap.nodes.credit.desc' },
      { id: 'dailyrcpt',  nameKey: 'sitemap.nodes.dailyrcpt.name',  page: 'DailyReceipt',    descKey: 'sitemap.nodes.dailyrcpt.desc' },
      { id: 'billrcpt',   nameKey: 'sitemap.nodes.billrcpt.name',   page: 'BillReceipt',     descKey: 'sitemap.nodes.billrcpt.desc' },
      { id: 'salereturn', nameKey: 'sitemap.nodes.salereturn.name', page: 'SaleReturn',      descKey: 'sitemap.nodes.salereturn.desc' },
    ]
  },

  { id: 'itemloss', nameKey: 'sitemap.nodes.itemloss.name', icon: '📉', page: 'ItemLoss',
    descKey: 'sitemap.nodes.itemloss.desc' },

  {
    id: 'purchase', nameKey: 'sitemap.nodes.purchase.name', icon: '🛒',
    descKey: 'sitemap.nodes.purchase.desc',
    children: [
      { id: 'po',       nameKey: 'sitemap.nodes.po.name',       page: 'PurchaseOrder',  descKey: 'sitemap.nodes.po.desc' },
      { id: 'expenses', nameKey: 'sitemap.nodes.expenses.name', page: 'Expenses',       descKey: 'sitemap.nodes.expenses.desc' },
      { id: 'pret',     nameKey: 'sitemap.nodes.pret.name',     page: 'PurchaseReturn', descKey: 'sitemap.nodes.pret.desc' },
    ]
  },

  {
    id: 'dip', nameKey: 'sitemap.nodes.dip.name', icon: '📏',
    descKey: 'sitemap.nodes.dip.desc',
    children: [
      { id: 'dipreading', nameKey: 'sitemap.nodes.dipreading.name', page: 'DipReading', descKey: 'sitemap.nodes.dipreading.desc' },
      { id: 'dipchart',   nameKey: 'sitemap.nodes.dipchart.name',   page: 'DipChart',   descKey: 'sitemap.nodes.dipchart.desc' },
      { id: 'fuelbal',    nameKey: 'sitemap.nodes.fuelbal.name',    page: 'FuelBalance', descKey: 'sitemap.nodes.fuelbal.desc' },
    ]
  },

  {
    id: 'accounting', nameKey: 'sitemap.nodes.accounting.name', icon: '📒',
    descKey: 'sitemap.nodes.accounting.desc',
    children: [
      { id: 'reports', nameKey: 'sitemap.nodes.reports.name', page: 'Accounting',          descKey: 'sitemap.nodes.reports.desc' },
      { id: 'coa',     nameKey: 'sitemap.nodes.coa.name',     page: 'Ledgers',             descKey: 'sitemap.nodes.coa.desc' },
      { id: 'lview',   nameKey: 'sitemap.nodes.lview.name',   page: 'LedgerView',          descKey: 'sitemap.nodes.lview.desc' },
      { id: 'pay',     nameKey: 'sitemap.nodes.pay.name',     page: 'Payments',            descKey: 'sitemap.nodes.pay.desc' },
      { id: 'contra',  nameKey: 'sitemap.nodes.contra.name',  page: 'Contra',              descKey: 'sitemap.nodes.contra.desc' },
      { id: 'journal', nameKey: 'sitemap.nodes.journal.name', page: 'Journal',             descKey: 'sitemap.nodes.journal.desc' },
      { id: 'fin',     nameKey: 'sitemap.nodes.fin.name',     page: 'FinancialStatements', descKey: 'sitemap.nodes.fin.desc' },
      { id: 'stockl',  nameKey: 'sitemap.nodes.stockl.name',  page: 'StockLedger',         descKey: 'sitemap.nodes.stockl.desc' },
      { id: 'bank',    nameKey: 'sitemap.nodes.bank.name',    page: 'Banking',             descKey: 'sitemap.nodes.bank.desc' },
      { id: 'recon',   nameKey: 'sitemap.nodes.recon.name',   page: 'Reconciliation',      descKey: 'sitemap.nodes.recon.desc' },
      { id: 'ot',      nameKey: 'sitemap.nodes.ot.name',      page: 'Overtime',            descKey: 'sitemap.nodes.ot.desc' },
    ]
  },

  { id: 'support', nameKey: 'sitemap.nodes.support.name', icon: '🎫', page: 'Support',
    descKey: 'sitemap.nodes.support.desc' },
  { id: 'register', nameKey: 'sitemap.nodes.register.name', icon: '➕', page: 'RegisterStation',
    descKey: 'sitemap.nodes.register.desc' },

  {
    id: 'admin', nameKey: 'sitemap.nodes.admin.name', icon: '🛡️',
    descKey: 'sitemap.nodes.admin.desc',
    children: [
      { id: 'audit',     nameKey: 'sitemap.nodes.audit.name',      page: 'AuditLog',       descKey: 'sitemap.nodes.audit.desc' },
      { id: 'usersroles',nameKey: 'sitemap.nodes.usersroles.name', page: 'UserManagement', descKey: 'sitemap.nodes.usersroles.desc' },
      { id: 'approvals', nameKey: 'sitemap.nodes.approvals.name',  page: 'Approvals',      descKey: 'sitemap.nodes.approvals.desc' },
    ]
  },
];

// ── Interactive Flowcharts ──────────────────────────────────────────────────
// Each step: { labelKey, detailKey, page?, type?: 'start'|'process'|'decision'|'end' }
export const FLOWS = [
  {
    id: 'station-setup', titleKey: 'sitemap.flows.station-setup.title', icon: '🏪',
    descKey: 'sitemap.flows.station-setup.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.station-setup.steps.0.label',  page: 'RegisterStation', detailKey: 'sitemap.flows.station-setup.steps.0.detail' },
      { type: 'decision',labelKey: 'sitemap.flows.station-setup.steps.1.label',  page: 'Approvals',       detailKey: 'sitemap.flows.station-setup.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.station-setup.steps.2.label',  page: 'UserManagement',  detailKey: 'sitemap.flows.station-setup.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.station-setup.steps.3.label',  page: 'Product',         detailKey: 'sitemap.flows.station-setup.steps.3.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.station-setup.steps.4.label',                           detailKey: 'sitemap.flows.station-setup.steps.4.detail' },
    ]
  },
  {
    id: 'master-setup', titleKey: 'sitemap.flows.master-setup.title', icon: '⚙️',
    descKey: 'sitemap.flows.master-setup.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.master-setup.steps.0.label',  page: 'Product',      detailKey: 'sitemap.flows.master-setup.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.master-setup.steps.1.label',  page: 'Tank',         detailKey: 'sitemap.flows.master-setup.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.master-setup.steps.2.label',  page: 'PumpList',     detailKey: 'sitemap.flows.master-setup.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.master-setup.steps.3.label',  page: 'NozzleConfig', detailKey: 'sitemap.flows.master-setup.steps.3.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.master-setup.steps.4.label',                        detailKey: 'sitemap.flows.master-setup.steps.4.detail' },
    ]
  },
  {
    id: 'meter-reading', titleKey: 'sitemap.flows.meter-reading.title', icon: '🔢',
    descKey: 'sitemap.flows.meter-reading.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.meter-reading.steps.0.label', page: 'MeterReading', detailKey: 'sitemap.flows.meter-reading.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.meter-reading.steps.1.label', detailKey: 'sitemap.flows.meter-reading.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.meter-reading.steps.2.label', detailKey: 'sitemap.flows.meter-reading.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.meter-reading.steps.3.label', detailKey: 'sitemap.flows.meter-reading.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.meter-reading.steps.4.label', detailKey: 'sitemap.flows.meter-reading.steps.4.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.meter-reading.steps.5.label', page: 'SaleList', detailKey: 'sitemap.flows.meter-reading.steps.5.detail' },
    ]
  },
  {
    id: 'sales', titleKey: 'sitemap.flows.sales.title', icon: '💰',
    descKey: 'sitemap.flows.sales.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.sales.steps.0.label', detailKey: 'sitemap.flows.sales.steps.0.detail' },
      { type: 'decision',labelKey: 'sitemap.flows.sales.steps.1.label', detailKey: 'sitemap.flows.sales.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.sales.steps.2.label', page: 'DailyReceipt', detailKey: 'sitemap.flows.sales.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.sales.steps.3.label', page: 'CreditSale',  detailKey: 'sitemap.flows.sales.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.sales.steps.4.label', detailKey: 'sitemap.flows.sales.steps.4.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.sales.steps.5.label', page: 'Accounting', detailKey: 'sitemap.flows.sales.steps.5.detail' },
    ]
  },
  {
    id: 'purchase', titleKey: 'sitemap.flows.purchase.title', icon: '🛒',
    descKey: 'sitemap.flows.purchase.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.purchase.steps.0.label',   page: 'PurchaseOrder', detailKey: 'sitemap.flows.purchase.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.purchase.steps.1.label',   detailKey: 'sitemap.flows.purchase.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.purchase.steps.2.label',   page: 'Tank',          detailKey: 'sitemap.flows.purchase.steps.2.detail' },
      { type: 'decision',labelKey: 'sitemap.flows.purchase.steps.3.label',   detailKey: 'sitemap.flows.purchase.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.purchase.steps.4.label',   page: 'Payments',      detailKey: 'sitemap.flows.purchase.steps.4.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.purchase.steps.5.label',   page: 'StockLedger',   detailKey: 'sitemap.flows.purchase.steps.5.detail' },
    ]
  },
  {
    id: 'tank-stock', titleKey: 'sitemap.flows.tank-stock.title', icon: '🛢️',
    descKey: 'sitemap.flows.tank-stock.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.tank-stock.steps.0.label', page: 'Tank',        detailKey: 'sitemap.flows.tank-stock.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.tank-stock.steps.1.label', page: 'PurchaseOrder', detailKey: 'sitemap.flows.tank-stock.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.tank-stock.steps.2.label', page: 'MeterReading', detailKey: 'sitemap.flows.tank-stock.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.tank-stock.steps.3.label', page: 'ItemLoss',    detailKey: 'sitemap.flows.tank-stock.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.tank-stock.steps.4.label', page: 'DipReading',  detailKey: 'sitemap.flows.tank-stock.steps.4.detail' },
      { type: 'decision',labelKey: 'sitemap.flows.tank-stock.steps.5.label', page: 'FuelBalance', detailKey: 'sitemap.flows.tank-stock.steps.5.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.tank-stock.steps.6.label', page: 'FuelBalance', detailKey: 'sitemap.flows.tank-stock.steps.6.detail' },
    ]
  },
  {
    id: 'credit-sale', titleKey: 'sitemap.flows.credit-sale.title', icon: '🧾',
    descKey: 'sitemap.flows.credit-sale.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.credit-sale.steps.0.label', page: 'Customer',    detailKey: 'sitemap.flows.credit-sale.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.credit-sale.steps.1.label', page: 'CreditSale', detailKey: 'sitemap.flows.credit-sale.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.credit-sale.steps.2.label', page: 'LedgerView', detailKey: 'sitemap.flows.credit-sale.steps.2.detail' },
      { type: 'decision',labelKey: 'sitemap.flows.credit-sale.steps.3.label', detailKey: 'sitemap.flows.credit-sale.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.credit-sale.steps.4.label', page: 'BillReceipt', detailKey: 'sitemap.flows.credit-sale.steps.4.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.credit-sale.steps.5.label', page: 'Accounting', detailKey: 'sitemap.flows.credit-sale.steps.5.detail' },
    ]
  },
  {
    id: 'accounting', titleKey: 'sitemap.flows.accounting.title', icon: '📒',
    descKey: 'sitemap.flows.accounting.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.accounting.steps.0.label', detailKey: 'sitemap.flows.accounting.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.accounting.steps.1.label', page: 'Ledgers',  detailKey: 'sitemap.flows.accounting.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.accounting.steps.2.label', page: 'Journal', detailKey: 'sitemap.flows.accounting.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.accounting.steps.3.label', page: 'Accounting', detailKey: 'sitemap.flows.accounting.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.accounting.steps.4.label', page: 'Reconciliation', detailKey: 'sitemap.flows.accounting.steps.4.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.accounting.steps.5.label', page: 'FinancialStatements', detailKey: 'sitemap.flows.accounting.steps.5.detail' },
    ]
  },
  {
    id: 'reporting', titleKey: 'sitemap.flows.reporting.title', icon: '📊',
    descKey: 'sitemap.flows.reporting.desc',
    steps: [
      { type: 'start',   labelKey: 'sitemap.flows.reporting.steps.0.label', detailKey: 'sitemap.flows.reporting.steps.0.detail' },
      { type: 'process', labelKey: 'sitemap.flows.reporting.steps.1.label', page: 'Accounting', detailKey: 'sitemap.flows.reporting.steps.1.detail' },
      { type: 'process', labelKey: 'sitemap.flows.reporting.steps.2.label', page: 'StockLedger', detailKey: 'sitemap.flows.reporting.steps.2.detail' },
      { type: 'process', labelKey: 'sitemap.flows.reporting.steps.3.label', page: 'FinancialStatements', detailKey: 'sitemap.flows.reporting.steps.3.detail' },
      { type: 'process', labelKey: 'sitemap.flows.reporting.steps.4.label', page: 'Banking', detailKey: 'sitemap.flows.reporting.steps.4.detail' },
      { type: 'end',     labelKey: 'sitemap.flows.reporting.steps.5.label', page: 'Dashboard', detailKey: 'sitemap.flows.reporting.steps.5.detail' },
    ]
  },
];
